import { ReactNode, useCallback, useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "../ui/sheet";
import { Separator } from "../ui/separator";
import { Toaster } from "../ui/sonner";
import { useViewStore } from "@/lib/stores/view";
import { useAuth } from "@/lib";

// Import logo image from Figma assets if available
// import logoImage from 'figma:asset/7ebe1feb3b9e3ef318c8f83e2c415d27a39be431.png';

interface PublicLayoutProps {
	children: ReactNode;
	onAdminAccess?: () => void;
	onNavigate?: (section: string) => void;
	currentSection?: string;
}

interface NavigationMenuProps {
	onNavigate: (section: string) => void;
	currentSection: string;
}

function NavigationMenu({ onNavigate, currentSection }: NavigationMenuProps) {
	const menuItems = [
		{ id: "search", label: "Tra cứu" },
		{ id: "template-2023", label: "Mẫu Văn Bằng 2023" },
		{ id: "template-bachelor", label: "Mẫu Văn Bằng Đại Học" },
		{ id: "template-master", label: "Mẫu Văn Bằng Thạc sĩ" },
	];

	return (
		// Only show on desktop - mobile uses hamburger menu
		<nav className='flex flex-grow h-full items-center'>
			{menuItems.map((item) => (
				<button
					key={item.id}
					onClick={() => onNavigate(item.id)}
					className={`text-sm whitespace-nowrap px-3 lg:px-4 py-3 transition-all text-white duration-200 hover:bg-[#D72134] ${currentSection === item.id ? "font-medium bg-[#D72134]" : ""}`}
				>
					{item.label}
				</button>
			))}
		</nav>
	);
}

function MobileMenu({ onNavigate, currentSection }: NavigationMenuProps & { onAdminAccess?: () => void }) {
	const [isOpen, setIsOpen] = useState(false);

	const menuItems = [
		{ id: "search", label: "Tra cứu văn bằng", icon: "🔍", description: "Tìm kiếm văn bằng, chứng chỉ" },
		{ id: "template-2023", label: "Mẫu Văn Bằng 2023", icon: "📄", description: "Xem mẫu văn bằng mới nhất" },
		{ id: "template-bachelor", label: "Mẫu Văn Bằng Đại Học", icon: "🎓", description: "Mẫu bằng đại học" },
		{ id: "template-master", label: "Mẫu Văn Bằng Thạc sĩ", icon: "🎖️", description: "Mẫu bằng thạc sĩ" },
	];

	const handleNavigate = (section: string) => {
		onNavigate(section);
		setIsOpen(false);
	};

	return (
		<Sheet open={isOpen} onOpenChange={setIsOpen}>
			<SheetTrigger asChild>
				<Button variant='ghost' size='sm' className='lg:hidden p-2 relative mobile-menu-trigger text-white'>
					<Menu className='h-5 w-5' />
					{/* Current section indicator */}
					{currentSection !== "search" && <div className='absolute -top-1 -right-1 w-2 h-2 bg-primary rounded-full animate-pulse'></div>}
				</Button>
			</SheetTrigger>
			<SheetContent side='right' className='w-80'>
				<SheetHeader>
					<SheetTitle className='text-left flex items-center gap-2'>
						<Menu className='h-5 w-5' />
						Menu điều hướng
					</SheetTitle>
				</SheetHeader>
				<div className='space-y-2 px-3'>
					{menuItems.map((item) => (
						<button
							key={item.id}
							onClick={() => handleNavigate(item.id)}
							className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors group hover:bg-[#D72134] hover:text-white ${currentSection === item.id ? "font-medium bg-[#D72134] text-white" : "text-muted-foreground hover:bg-[#D72134]"
								}`}
						>
							<span className='text-lg flex-shrink-0 mt-0.5'>{item.icon}</span>
							<div className='flex-1 min-w-0'>
								<div className='font-medium'>{item.label}</div>
								<div className='text-xs mt-1'>{item.description}</div>
							</div>
						</button>
					))}

					<Separator className='bg-sidebar-border' />

					{/* Admin Portal note */}
					<div className='flex items-center gap-2 px-4 py-3 rounded-lg hover:bg-[#D72134] hover:text-white'>
						<span className='text-sm'>💻</span>
						<div>
							<p className='font-medium'>Admin Portal</p>
							<p className='text-xs'>Chỉ khả dụng trên máy tính</p>
						</div>
					</div>
				</div>
			</SheetContent>
		</Sheet>
	);
}

export default function PublicLayout({ children, onAdminAccess, onNavigate, currentSection = "search" }: PublicLayoutProps) {
	const [visible, setVisible] = useState(false);
	const { currentView, setCurrentView } = useViewStore();
	const { isAuthenticated } = useAuth();

	useEffect(() => {
		const t = setTimeout(() => setVisible(true), 0);
		return () => clearTimeout(t);
	}, []);

	return (
		<>
			<div className='min-h-screen bg-background flex flex-col'>
				<header className='border-b backdrop-blur-sm sticky top-0 z-50 bg-[#1F2251]'>
					<div className='max-w-7xl mx-auto px-4 sm:px-6'>
						<div className='flex justify-between gap-4'>
							{/* Logo and Title */}
							<div className='flex items-center gap-3 sm:gap-4 min-w-0 lg:flex-shrink-0 lg:w-auto'>
								<img
									src='https://www.vlu.edu.vn/_next/static/media/logo.8e5334db.svg'
									alt='Đại học Văn Lang'
									className='h-8 sm:h-10 lg:h-11 w-auto flex-shrink-0'
									onError={(e) => {
										// Fallback if logo fails to load
										e.currentTarget.style.display = "none";
									}}
								/>
								{/* <div className='min-w-0 lg:min-w-0'>
									<h1 className='text-base sm:text-lg lg:text-xl font-semibold text-foreground truncate'>Hệ thống Tra cứu</h1>
									<p className='text-xs sm:text-sm lg:text-sm text-muted-foreground truncate'>Văn bằng - Chứng chỉ - Khen thưởng</p>
								</div> */}
							</div>

							{/* Desktop Navigation - Center */}
							<div className='hidden h-full lg:flex items-center justify-center flex-1'>
								<NavigationMenu onNavigate={onNavigate || (() => { })} currentSection={currentSection} />
							</div>

							{/* Desktop Admin Portal - Right */}
							<div className='hidden lg:flex items-center'>
								{isAuthenticated ? (
									<button onClick={() => {
										setCurrentView("admin");
									}} className='text-sm hover:bg-[#D72134] text-white transition-colors whitespace-nowrap px-4 py-3'>
										Đến Trang Quản Trị
									</button>
								) :
									<button onClick={onAdminAccess} className='text-sm hover:bg-[#D72134] text-white transition-colors whitespace-nowrap px-4 py-3'>
										Đăng Nhập
									</button>
								}
							</div>

							{/* Mobile Menu Button */}
							<MobileMenu onNavigate={onNavigate || (() => { })} currentSection={currentSection} onAdminAccess={onAdminAccess} />
						</div>
					</div>
				</header>

				<main className='flex-grow bg-[#F5F8FA] duration-100 ease-out' style={{ opacity: visible === false ? '0' : '1' }}>{children}</main>

				<footer className='bg-[#1f2251] text-white mp-8 sm:pt-12 duration-100 ease-out' style={{ opacity: visible === false ? '0' : '1' }}>
					<div className='max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8'>
						<div className='grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8'>
							<div className=''>
								<p className='text-xs sm:text-sm text-white/80 leading-relaxed'>
									<span className='text-white font-medium'>Cơ sở chính</span> : 69/68 Đặng Thùy Trâm, Phường Bình Lợi Trung, TP.HCM
								</p>
								<p className='text-xs sm:text-sm text-white/80 leading-relaxed'>
									<span className='text-white font-medium'>Cơ sở 1</span> : 45 Nguyễn Khắc Nhu, Phường Cầu Ông Lãnh, TP.HCM
								</p>
								<p className='text-xs sm:text-sm text-white/80 leading-relaxed'>
									<span className='text-white font-medium'>Cơ sở 2</span> : 233A Phan Văn Trị, Phường Bình Lợi Trung, TP.HCM
								</p>
								{/* <p className='text-xs sm:text-sm text-white/80 leading-relaxed'>
									<span className='text-white font-medium'>Ký túc xá</span> : 160/63A-B, Phan Huy Ích, P.12, Q. Gò Vấp, TP. HCM
								</p> */}
							</div>
							<div className='lg:text-right'>
								<p className='text-xs sm:text-sm text-white/80 leading-relaxed'>Trang thông tin điện tử tổng hợp Trường Đại học Văn Lang</p>
								<p className='text-xs sm:text-sm text-white/80 leading-relaxed'>Hoạt động theo giấy phép số 75/GP-ICP-STTTT</p>
								<p className='text-xs sm:text-sm text-white/80 leading-relaxed'>Tổng biên tập: Hiệu trưởng</p>
							</div>
						</div>

						{/* Mobile-friendly copyright */}
						<div className='border-t border-white/20 mt-6 pt-4'>
							<div className='grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 text-xs text-white/60'>
								<p className=''>
									© 2023 {" "}
									<a href='https://www.vlu.edu.vn/vi' target='_blank' className='hover:underline'>
										Trường Đại Học Văn Lang
									</a>
								</p>
								<p className='lg:text-right'>
									Phát triển bởi{" "}
									<a href='https://itconnect.vlu.edu.vn/' target='_blank' className='hover:underline'>
										Phòng Công Nghệ Thông Tin
									</a>
								</p>
							</div>
						</div>
					</div>
				</footer>
			</div>
			<Toaster expand={true} />
		</>
	);
}
